import { computed, type Ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services'
import { type ActionButton, CRUD, DataType } from '@/interfaces'
import { openModalCustomConfirmation } from '@/services/utils'
import { ModalType } from '@/components/modals/modals-util'
import router from '@/router'
import { useVideoDraftsEdit } from '@/composables/useVideoDraftsEdit/useVideoDraftsEdit'
import { VideoDraft } from '../../services/video-drafts/types'

interface EditConfig<T extends { id: number }, TEdit = unknown> {
  routeName: string
  getCachedItems: (edit: TEdit) => T[]
  setItemsToEdit: (items: T[], edit: TEdit) => void
}

type CachedItem<T extends { id: number }> = T

const dataTypeConfigs = {
  [DataType.VideoDraft]: {
    modalType: ModalType.VIDEOS_DELETE_VIDEO_DRAFT_MODAL,
    deleteMethod: (ids: number[]) => apiService.videoDrafts.deleteVideoDrafts(ids),
    deleteSuccessMessage: {
      single: 'video-deleted',
      multiple: 'videos-deleted'
    },
    supportedActions: ['edit', 'delete'] as const,
    edit: {
      routeName: 'add-video',
      getCachedItems: (draftEdit: ReturnType<typeof useVideoDraftsEdit>) => {
        return draftEdit.getCachedDrafts()
      },
      setItemsToEdit: (items: VideoDraft[], draftEdit: ReturnType<typeof useVideoDraftsEdit>) => {
        draftEdit.setDraftsToEdit(items)
      }
    } as EditConfig<VideoDraft, ReturnType<typeof useVideoDraftsEdit>>
  }
}

export function useTableSelectionActions<Dt extends DataType>(
  dataType: Ref<Dt>,
  selectedIds: Ref<number[]>,
  draftEdit?: ReturnType<typeof useVideoDraftsEdit>
) {
  const store = useStore()
  const { t } = useI18n()

  const config = computed(() => {
    if (dataType.value in dataTypeConfigs) {
      return dataTypeConfigs[dataType.value as keyof typeof dataTypeConfigs]
    }
    return undefined
  })

  const bulkDelete = async (): Promise<void> => {
    if (!config.value) {
      return
    }

    const ids = selectedIds.value
    if (ids.length === 0) {
      return
    }

    try {
      await config.value.deleteMethod(ids)

      const messageKey =
        ids.length === 1
          ? config.value.deleteSuccessMessage.single
          : config.value.deleteSuccessMessage.multiple

      const messageParams = ids.length === 1 ? {} : { number: ids.length }

      store.dispatch('messages/addMessage', {
        message: t(messageKey, messageParams),
        type: 'success'
      })
    } catch (err: unknown) {
      console.warn(err)
      throw err
    }
  }

  const openDeleteConfirmation = (): Promise<void> => {
    return new Promise((resolve) => {
      if (!config.value) {
        resolve()
        return
      }
      const itemsNumber = selectedIds.value.length
      if (itemsNumber === 0) {
        resolve()
        return
      }

      openModalCustomConfirmation(
        {
          action: itemsNumber > 1 ? CRUD.MultipleDelete : CRUD.Delete,
          type: dataType.value,
          itemsNumber,
          hideInput: true,
          onConfirm: async () => {
            await bulkDelete()
            resolve()
          }
        },
        config.value.modalType
      )
    })
  }

  const bulkEdit = (): void => {
    if (!config.value?.edit || !draftEdit) {
      console.warn('Edit functionality not available')
      return
    }
  
    const editConfig = config.value.edit
    const cached = editConfig.getCachedItems(draftEdit)
    const selected = cached.filter(
      (item): item is CachedItem<typeof cached[number]> =>
        item != null && selectedIds.value.includes(item.id)
    )

    if (selected.length === 0) return
    editConfig.setItemsToEdit(selected, draftEdit)
    router.push({ name: editConfig.routeName })
  }

  const bulkActions = computed<ActionButton[] | undefined>(() => {
    if (!config.value) return undefined
    
    const actions: ActionButton[] = []

    if (config.value.supportedActions.includes('edit')) {
      actions.push({
        icon: 'editIcon',
        label: 'edit',
        onClick: bulkEdit
      })
    }

    if (config.value.supportedActions.includes('delete')) {
      actions.push({
        icon: 'deleteIcon',
        label: 'delete',
        onClick: openDeleteConfirmation
      })
    }

    return actions.length ? actions : undefined
  })

  return { bulkActions }
}